"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
type Lang = "vi" | "en";
function useLocale(): Lang {
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") return stored;
    }
    return "vi";
  });
  useEffect(() => {
    const handleLangChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: Lang;
      }>;
      const nextLang = customEvent.detail?.lang;
      if (nextLang === "vi" || nextLang === "en") {
        setLang(nextLang);
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "roboto_lang" && (event.newValue === "vi" || event.newValue === "en")) {
        setLang(event.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  return lang;
}
const UI_TEXT = {
  hubBadge: {
    vi: "Trung tâm Tài nguyên Phụ huynh",
    en: "Parent Resource Hub"
  },
  sectionTitle: {
    vi: "Cẩm nang đồng hành cùng kỹ sư nhí",
    en: "Parenting guides for young engineers"
  },
  sectionSubtitle: {
    vi: "Định hướng rõ ràng giúp ba mẹ chọn đúng lộ trình, xây dựng tư duy STEM vững chắc và khơi dậy đam mê công nghệ tại nhà.",
    en: "Clear advice to help you choose the right path, build foundational STEM habits, and support your child at home."
  },
  loadError: {
    vi: "Hiện tại không thể tải danh sách cẩm nang.",
    en: "Unable to load current articles at this time."
  },
  tryAgain: {
    vi: "Thử lại",
    en: "Try again"
  },
  readGuide: {
    vi: "Đọc cẩm nang",
    en: "Read guide"
  },
  previous: {
    vi: "Trước",
    en: "Previous"
  },
  next: {
    vi: "Tiếp theo",
    en: "Next"
  },
  allCategories: {
    vi: "Tất cả",
    en: "All"
  },
  noMatch: {
    vi: "Không có cẩm nang nào thuộc danh mục này.",
    en: "No articles found in this category."
  },
  showAll: {
    vi: "Xem tất cả",
    en: "View all"
  },
  featuredBadge: {
    vi: "Tiêu điểm",
    en: "Featured"
  }
};
const defaultArticles = [{
  id: "art-1",
  title: {
    vi: "Coding hay Robotics: trẻ 6 tuổi nên bắt đầu từ lộ trình nào?",
    en: "Coding or robotics: which path should a 6-year-old start with?"
  },
  excerpt: {
    vi: "Hiểu rõ sự khác biệt phát triển giữa việc lắp ráp phần cứng cơ khí thực tế và rèn luyện tư duy thuật toán khối từ sớm.",
    en: "Understand the key developmental differences between physical hardware builds and early block-based algorithmic thinking."
  },
  category: {
    vi: "Định hướng Lộ trình",
    en: "Program Selection"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/stem_guidance_coding_robotics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/stem_guidance_coding_robotics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/stem_guidance_coding_robotics.png-wvc-sizes",
  link: "#"
}, {
  id: "art-2",
  title: {
    vi: "Cách Robotics cơ khí biến toán học trừu tượng thành trực giác thực tế",
    en: "How mechanical robotics turns abstract math into physical intuition"
  },
  excerpt: {
    vi: "Khám phá cách tỷ số truyền bánh răng, góc quay và vòng phản hồi cảm biến giúp biến các định lý toán học và hình học thành phản xạ tự nhiên.",
    en: "Discover how gear ratios, angular turns, and sensor feedback loops anchor core arithmetic and geometry principles."
  },
  category: {
    vi: "Phương pháp STEM",
    en: "STEM Learning"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_math_intuition.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_math_intuition.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_math_intuition.png-wvc-sizes",
  link: "#"
}, {
  id: "art-3",
  title: {
    vi: "Thời gian nhìn màn hình và thời gian sáng tạo: từ người dùng sang nhà sáng chế",
    en: "Screen time vs creation time: shifting from consumers to inventors"
  },
  excerpt: {
    vi: "Khung phương pháp thực hành tại nhà giúp chuyển thói quen xem thiết bị điện tử thụ động thành năng lực kiến tạo công nghệ chủ động.",
    en: "Practical home frameworks to redirect passive digital consumption toward active computational building."
  },
  category: {
    vi: "Thói quen Công nghệ",
    en: "Digital Wellness"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/screen_time_creation_lab.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/screen_time_creation_lab.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/screen_time_creation_lab.png-wvc-sizes",
  link: "#"
}, {
  id: "art-4",
  title: {
    vi: "Bên trong đấu trường Robotics thanh thiếu niên: điều phụ huynh cần chuẩn bị",
    en: "Inside youth competitive robotics: what parents should expect"
  },
  excerpt: {
    vi: "Cẩm nang toàn diện về phối hợp đồng đội thi đấu, kế hoạch tập luyện và cách các giải đấu quốc tế rèn luyện bản lĩnh vượt khó.",
    en: "A complete guide to tournament team dynamics, time commitments, and how national leagues build resilience."
  },
  category: {
    vi: "Đấu trường & Giải đấu",
    en: "Competitions"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/youth_robotics_competition_team.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/youth_robotics_competition_team.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/youth_robotics_competition_team.png-wvc-sizes",
  link: "#"
}, {
  id: "art-5",
  title: {
    vi: "Từ LEGO Robotics đến Python: khi nào học sinh sẵn sàng bước tiếp?",
    en: "LEGO robotics to Python: when is your student ready to transition?"
  },
  excerpt: {
    vi: "Nhận biết các cột mốc cho thấy trẻ đã làm chủ tư duy khối lệnh trực quan và sẵn sàng chinh phục cú pháp dòng lệnh chuyên sâu.",
    en: "Explore the milestones indicating your child has outgrown visual blocks and is primed for text-based syntax."
  },
  category: {
    vi: "Lộ trình Chuyên sâu",
    en: "Curriculum Tracks"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/lego_to_python_transition.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/lego_to_python_transition.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/lego_to_python_transition.png-wvc-sizes",
  link: "#"
}, {
  id: "art-6",
  title: {
    vi: "Rèn luyện tư duy Engineering thông qua quá trình thử nghiệm và sửa lỗi",
    en: "Fostering an engineering mindset through iterative project failure"
  },
  excerpt: {
    vi: "Cách thức gỡ lỗi bài bản trong phòng lab rèn cho con tính kiên định, xem mỗi lần chưa hoàn thiện là một dữ kiện học hỏi quý giá.",
    en: "How constructive debugging in a structured lab teaches children to treat mistakes as diagnostic clues."
  },
  category: {
    vi: "Đồng hành cùng Con",
    en: "Parent Guidance"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/engineering_mindset_mentorship.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/engineering_mindset_mentorship.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/engineering_mindset_mentorship.png-wvc-sizes",
  link: "#"
}];
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-sizes",
  alt: ""
};
function formatPostDate(dateStr?: string, currentLang: Lang = "vi") {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(currentLang === "vi" ? "vi-VN" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return null;
  }
}
function parsePostData(item: any, lang: Lang) {
  const isWpPost = Boolean(item.content || item.date || item.slug);
  const title = isWpPost ? item.title?.rendered || item.title || "" : item.title?.[lang] || item.title?.vi || "";
  const rawExcerpt = isWpPost ? item.excerpt?.rendered || item.excerpt || "" : item.excerpt?.[lang] || item.excerpt?.vi || "";
  const cleanExcerpt = rawExcerpt ? rawExcerpt.replace(/<[^>]+>/g, "").replace(/\[&hellip;\]/g, "…").replace(/\[\.\.\.\]/g, "…").trim() : "";
  const targetLink = item.link || "#";
  let categoryName = "";
  if (isWpPost) {
    if (Array.isArray(item.categories) && item.categories.length > 0) {
      const firstCat = item.categories[0];
      categoryName = typeof firstCat === "string" ? firstCat : firstCat?.name || "";
    } else if (typeof item.category === "string") {
      categoryName = item.category;
    }
  } else {
    categoryName = item.category?.[lang] || item.category?.vi || "";
  }
  const dateStr = isWpPost && item.date ? item.date : undefined;
  const formattedDate = formatPostDate(dateStr, lang);
  const imageSrc = isWpPost ? item.featuredImage : item.image;
  const imageSrcset = isWpPost ? undefined : item.srcset;
  const imageSizes = isWpPost ? undefined : item.sizes;
  return {
    id: item.id,
    title,
    excerpt: cleanExcerpt,
    targetLink,
    categoryName: categoryName.trim(),
    formattedDate,
    imageSrc,
    imageSrcset,
    imageSizes,
    isWpPost
  };
}
function ArticlesGridConsumer() {
  const lang = useLocale();
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();

  // Read initial category from URL search params or hash
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const queryCat = searchParams.get("category") || searchParams.get("cat");
      if (queryCat) return decodeURIComponent(queryCat);
      const rawHash = window.location.hash.replace(/^#/, "");
      if (rawHash) {
        if (rawHash.startsWith("category=")) {
          return decodeURIComponent(rawHash.slice("category=".length));
        }
        const hashParams = new URLSearchParams(rawHash);
        const catInHash = hashParams.get("category") || hashParams.get("cat");
        if (catInHash) return decodeURIComponent(catInHash);
        if (rawHash !== "latest-articles-guides") {
          return decodeURIComponent(rawHash);
        }
      }
    }
    return "ALL";
  });
  useEffect(() => {
    const handleUrlChange = () => {
      if (typeof window === "undefined") return;
      const searchParams = new URLSearchParams(window.location.search);
      const queryCat = searchParams.get("category") || searchParams.get("cat");
      if (queryCat) {
        setSelectedCategory(decodeURIComponent(queryCat));
        return;
      }
      const rawHash = window.location.hash.replace(/^#/, "");
      if (rawHash) {
        if (rawHash.startsWith("category=")) {
          setSelectedCategory(decodeURIComponent(rawHash.slice("category=".length)));
          return;
        }
        const hashParams = new URLSearchParams(rawHash);
        const catInHash = hashParams.get("category") || hashParams.get("cat");
        if (catInHash) {
          setSelectedCategory(decodeURIComponent(catInHash));
          return;
        }
        if (rawHash !== "latest-articles-guides") {
          setSelectedCategory(decodeURIComponent(rawHash));
        }
      }
    };
    window.addEventListener("hashchange", handleUrlChange);
    window.addEventListener("popstate", handleUrlChange);
    return () => {
      window.removeEventListener("hashchange", handleUrlChange);
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);
  const hasLivePosts = Boolean(posts && posts.length > 0);
  const items = hasLivePosts ? posts : defaultArticles;

  // Derive unique categories from live posts or fallback items
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    if (hasLivePosts) {
      posts.forEach((item: any) => {
        if (Array.isArray(item.categories) && item.categories.length > 0) {
          item.categories.forEach((cat: any) => {
            const catName = typeof cat === "string" ? cat.trim() : cat?.name?.trim();
            if (catName) categorySet.add(catName);
          });
        } else if (typeof item.category === "string" && item.category.trim()) {
          categorySet.add(item.category.trim());
        }
      });
    } else {
      defaultArticles.forEach(art => {
        const catName = (art.category[lang] || art.category.vi || "").trim();
        if (catName) categorySet.add(catName);
      });
    }
    return Array.from(categorySet);
  }, [hasLivePosts, posts, lang]);

  // Normalize selected category with fallback to "ALL"
  const activeCategory = useMemo(() => {
    if (selectedCategory === "ALL" || categories.length === 0) return selectedCategory;
    if (categories.includes(selectedCategory)) return selectedCategory;
    const caseMatch = categories.find(c => c.toLowerCase() === selectedCategory.toLowerCase());
    if (caseMatch) return caseMatch;
    return "ALL";
  }, [selectedCategory, categories]);

  // Filter items client-side
  const filteredItems = useMemo(() => {
    if (activeCategory === "ALL") return items;
    return items.filter((item: any) => {
      const isWpPost = Boolean(item.content || item.date || item.slug);
      if (isWpPost) {
        if (Array.isArray(item.categories) && item.categories.length > 0) {
          return item.categories.some((cat: any) => {
            const catName = typeof cat === "string" ? cat.trim() : cat?.name?.trim();
            return catName === activeCategory;
          });
        }
        const singleCat = typeof item.category === "string" ? item.category.trim() : item.category?.name?.trim();
        return singleCat === activeCategory;
      }
      const fallbackCat = (item.category?.[lang] || item.category?.vi || "").trim();
      return fallbackCat === activeCategory;
    });
  }, [items, activeCategory, lang]);
  if (loading) {
    return <div className="space-y-8">
        {/* Featured Skeleton */}
        <div className="overflow-hidden rounded-xl border border-border bg-card p-6 md:p-8 animate-pulse">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="h-64 rounded-lg bg-muted lg:col-span-7" />
            <div className="flex flex-col justify-center space-y-4 lg:col-span-5">
              <div className="h-4 w-1/4 rounded bg-muted" />
              <div className="h-8 w-3/4 rounded bg-muted" />
              <div className="h-16 w-full rounded bg-muted" />
              <div className="h-10 w-32 rounded bg-muted" />
            </div>
          </div>
        </div>

        {/* Varied Grid Skeletons */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          <div className="flex flex-col rounded-xl border border-border bg-card p-6 md:col-span-7 animate-pulse">
            <div className="h-44 w-full rounded-lg bg-muted" />
            <div className="mt-4 h-6 w-2/3 rounded bg-muted" />
            <div className="mt-3 h-12 w-full rounded bg-muted" />
          </div>
          <div className="flex flex-col rounded-xl border border-border bg-card p-6 md:col-span-5 animate-pulse">
            <div className="h-44 w-full rounded-lg bg-muted" />
            <div className="mt-4 h-6 w-2/3 rounded bg-muted" />
            <div className="mt-3 h-12 w-full rounded bg-muted" />
          </div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-base text-muted-foreground">
          {UI_TEXT.loadError[lang]}
        </p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4 border-border text-foreground hover:border-secondary hover:bg-secondary/10">
          {UI_TEXT.tryAgain[lang]}
        </Button>
      </div>;
  }
  const featuredRaw = filteredItems.length > 0 ? filteredItems[0] : null;
  const supportingRaw = filteredItems.length > 1 ? filteredItems.slice(1) : [];
  const featuredData = featuredRaw ? parsePostData(featuredRaw, lang) : null;

  // Varied spans for supporting grid cards (asymmetric layout)
  const getCardSpan = (index: number) => {
    const pattern = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-6", "md:col-span-6"];
    return pattern[index % pattern.length];
  };
  return <div>
      {/* Category filter chips */}
      {categories.length > 0 && <div className="mb-10 flex flex-wrap items-center gap-2">
          <Button type="button" variant={activeCategory === "ALL" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("ALL")} className={activeCategory === "ALL" ? "bg-primary text-primary-foreground border-transparent font-medium shadow-xs" : "border-border bg-card text-muted-foreground hover:border-secondary hover:text-foreground"}>
            {UI_TEXT.allCategories[lang]}
          </Button>

          {categories.map((cat, idx) => {
        const isSelected = activeCategory === cat;
        return <Button key={cat} data-index={idx} type="button" variant={isSelected ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)} className={isSelected ? "bg-primary text-primary-foreground border-transparent font-medium shadow-xs" : "border-border bg-card text-muted-foreground hover:border-secondary hover:text-foreground"}>
                {cat}
              </Button>;
      })}
        </div>}

      {/* Empty State */}
      {filteredItems.length === 0 ? <div className="rounded-xl border border-border bg-card p-12 text-center">
          <p className="text-base text-muted-foreground">
            {UI_TEXT.noMatch[lang]}
          </p>
          <Button variant="outline" onClick={() => setSelectedCategory("ALL")} className="mt-4 border-border text-foreground hover:border-secondary hover:bg-secondary/10">
            {UI_TEXT.showAll[lang]}
          </Button>
        </div> : <div className="space-y-10">
          {/* (a) Large Featured Article Block */}
          {featuredData && <Card className="group overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow,transform] duration-250 hover:-translate-y-1 hover:border-secondary hover:shadow-md">
              <div className="grid grid-cols-1 items-stretch lg:grid-cols-12">
                {featuredData.imageSrc && <div className="relative aspect-16/10 overflow-hidden bg-muted lg:col-span-7 lg:aspect-auto">
                    {featuredData.imageSrcset && featuredData.imageSizes ? <img src={featuredData.imageSrc} data-wvc-srcset={featuredData.imageSrcset} data-wvc-sizes={featuredData.imageSizes} alt={featuredData.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" /> : <img src={featuredData.imageSrc} alt={featuredData.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" />}

                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      <span className="inline-flex items-center rounded-md bg-primary px-2.5 py-0.75 font-mono text-xs font-semibold text-primary-foreground shadow-2xs">
                        {UI_TEXT.featuredBadge[lang]}
                      </span>
                      {featuredData.categoryName && <span className="inline-flex items-center rounded-lg border border-secondary/40 bg-card/95 px-3 py-1 font-mono text-xs font-semibold text-secondary backdrop-blur-xs shadow-2xs">
                          {featuredData.categoryName}
                        </span>}
                    </div>
                  </div>}

                <div className={`flex flex-col justify-between min-w-0 p-6 sm:p-8 lg:p-10 ${featuredData.imageSrc ? "lg:col-span-5" : "lg:col-span-12"}`}>
                  <div className="min-w-0">
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      {!featuredData.imageSrc && featuredData.categoryName && <span className="inline-flex items-center rounded-lg border border-secondary/30 bg-secondary/10 px-3 py-1 font-mono text-xs font-semibold text-secondary">
                          {featuredData.categoryName}
                        </span>}
                      {featuredData.formattedDate && <div className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                          <SafeHydrate><Calendar className="size-3.5 text-secondary" /></SafeHydrate>
                          <span>{featuredData.formattedDate}</span>
                        </div>}
                    </div>

                    <h3 className="text-2xl font-bold font-serif leading-tight text-foreground transition-colors group-hover:text-primary sm:text-3xl">
                      <Link to={featuredData.targetLink} className="hover:underline">
                        {featuredData.title}
                      </Link>
                    </h3>

                    {featuredData.excerpt && <p className="mt-4 line-clamp-4 text-base leading-relaxed text-muted-foreground">
                        {featuredData.excerpt}
                      </p>}
                  </div>

                  <div className="mt-8 border-t border-border/70 pt-6">
                    <Button asChild variant="default" className="bg-primary px-6 py-2.5 font-medium text-primary-foreground shadow-xs hover:bg-primary/90">
                      <Link to={featuredData.targetLink}>
                        <span>{UI_TEXT.readGuide[lang]}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </Card>}

          {/* (b) Varied Supporting Grid */}
          {supportingRaw.length > 0 && <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
              {supportingRaw.map((item: any, i: number) => {
          const cardData = parsePostData(item, lang);
          const spanClass = getCardSpan(i);
          return <Card key={cardData.id || i} data-index={i} className={`group flex flex-col justify-between min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow,transform] duration-250 hover:-translate-y-1 hover:border-secondary hover:shadow-md ${spanClass}`}>
                    <div className="min-w-0">
                      {cardData.imageSrc && <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                          {cardData.imageSrcset && cardData.imageSizes ? <img src={cardData.imageSrc} data-wvc-srcset={cardData.imageSrcset} data-wvc-sizes={cardData.imageSizes} alt={cardData.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" /> : <img src={cardData.imageSrc} alt={cardData.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-102" />}

                          {cardData.categoryName && <div className="absolute top-3 left-3">
                              <span className="inline-flex items-center rounded-lg border border-secondary/30 bg-card/95 px-3 py-1 font-mono text-xs font-semibold text-secondary backdrop-blur-xs shadow-2xs">
                                {cardData.categoryName}
                              </span>
                            </div>}
                        </div>}

                      <CardHeader className="pt-6">
                        <div className="flex flex-wrap items-center gap-2">
                          {!cardData.imageSrc && cardData.categoryName && <span className="inline-flex items-center rounded-lg border border-secondary/30 bg-secondary/10 px-2.5 py-0.5 font-mono text-xs font-semibold text-secondary">
                              {cardData.categoryName}
                            </span>}

                          {cardData.formattedDate && <div className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                              <SafeHydrate><Calendar className="size-3.5 text-secondary" /></SafeHydrate>
                              <span>{cardData.formattedDate}</span>
                            </div>}
                        </div>

                        <CardTitle className="mt-2 text-lg font-bold font-serif leading-snug text-foreground transition-colors group-hover:text-primary">
                          <Link to={cardData.targetLink} className="hover:underline">
                            {cardData.title}
                          </Link>
                        </CardTitle>
                      </CardHeader>

                      {cardData.excerpt && <CardContent className="min-w-0">
                          <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                            {cardData.excerpt}
                          </p>
                        </CardContent>}
                    </div>

                    <CardFooter className="pt-2 pb-6">
                      <Button asChild variant="outline" className="w-full justify-between border-border text-foreground transition-[border-color,background-color] hover:border-secondary hover:bg-secondary/10 hover:text-foreground">
                        <Link to={cardData.targetLink}>
                          <span>{UI_TEXT.readGuide[lang]}</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>;
        })}
            </div>}
        </div>}

      {/* Pagination controls */}
      {(hasNext || hasPrev) && <div className="mt-12 flex items-center justify-center gap-4">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="border-border text-foreground hover:border-secondary hover:bg-secondary/10">
            {UI_TEXT.previous[lang]}
          </Button>
          <Button variant="default" onClick={nextPage} disabled={!hasNext} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {UI_TEXT.next[lang]}
          </Button>
        </div>}
    </div>;
}
export default function Articles() {
  const lang = useLocale();
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date",
    status: ["publish"]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-articles-guides" data-nav="light" className="bg-background py-20 text-foreground">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
        {/* Static Floating Decorative Mascot */}
        <div aria-hidden="true" className="pointer-events-none select-none absolute top-0 right-5 sm:right-8 lg:right-16 z-0 hidden sm:block">
          <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt="" loading="lazy" decoding="async" className="h-24 w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 object-contain bg-transparent drop-shadow-md pointer-events-none select-none" />
        </div>

        {/* Lab Blueprint spec rail accent */}
        <div className="mb-4 flex items-center gap-2">
          <div className="h-1.5 w-1.5 bg-secondary" />
          <div className="h-px w-12 bg-secondary" />
          <span className="font-mono text-xs font-semibold tracking-widest text-secondary uppercase">
            {UI_TEXT.hubBadge[lang]}
          </span>
        </div>

        <div className="mb-12 max-w-3xl pr-4 sm:pr-32 md:pr-36 lg:pr-40">
          <h2 className="text-3xl font-bold tracking-tight font-serif text-foreground sm:text-4xl">
            {UI_TEXT.sectionTitle[lang]}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {UI_TEXT.sectionSubtitle[lang]}
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="articles-posts-provider">
          <ArticlesGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}